import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth, useUser } from '@clerk/clerk-react'
import { Loader2 } from 'lucide-react'
import { useApi } from '@/hooks/use-api'

/**
 * KNWN4 staff — always skip both gates (onboarding + payment) on the customer
 * dashboard, regardless of D1 state. Belt-and-suspenders so a missing/uncomped
 * staff row can never lock a team member out of their own product. Customer
 * accounts (incl. test accounts) are NOT here and go through the real flow.
 * Keep in sync with the backend STAFF_USER_IDS allowlist.
 */
const STAFF_EMAILS: ReadonlySet<string> = new Set([
  'knwn4official@gmail.com',
  'taha.a.hashmi@gmail.com',
  'eli@knwn4.com',
  'eli.tanenbaum@gmail.com',
  'tj@knwn4.com',
])

/**
 * The single gate that orders a customer's first-run journey:
 *
 *   signed in  →  ONBOARDING  →  PAYMENT  →  DASHBOARD
 *
 * (Product decision 2026-06-12: onboarding runs BEFORE the payment wall.)
 *
 * Both stages are driven by server truth from /api/auth/me:
 *   - onboarding_completed === 1  → onboarding done
 *   - entitled === true           → comped OR has a live Stripe subscription
 *
 * Staff / comp accounts have entitled=true, so they skip the payment wall
 * automatically — no email allowlist needed here anymore. The old
 * ProtectedRoute admin-email gate is gone; this guard is the only gate.
 */

type UserData = {
  user: {
    plan_status: string
    onboarding_completed: number
    entitled?: boolean
  }
}

// How long to wait for the Stripe webhook to flip `entitled` after the user
// returns from Checkout, before giving up and letting the normal gate run.
const POST_CHECKOUT_MAX_POLLS = 8 // × 3s ≈ 24s

export function AccessGuard({ children }: { readonly children: React.ReactNode }) {
  const { isSignedIn } = useAuth()
  const { user: clerkUser } = useUser()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const api = useApi()

  const email = clerkUser?.primaryEmailAddress?.emailAddress?.toLowerCase()
  const isStaff = !!email && STAFF_EMAILS.has(email)

  // Just back from Stripe Checkout? The subscription webhook may not have
  // landed yet, so we poll /auth/me instead of bouncing them to /billing.
  const justPaid = searchParams.get('checkout') === 'success'
  const [polls, setPolls] = useState(0)
  const stillWaiting = justPaid && polls < POST_CHECKOUT_MAX_POLLS

  const { data, isLoading } = useQuery({
    queryKey: ['user-me'],
    queryFn: () => api<UserData>('/api/auth/me'),
    enabled: !!isSignedIn,
    staleTime: justPaid ? 0 : 5_000,
    refetchInterval: stillWaiting ? 3_000 : false,
  })

  useEffect(() => {
    if (!stillWaiting) return
    const t = setInterval(() => setPolls((p) => p + 1), 3_000)
    return () => clearInterval(t)
  }, [stillWaiting])

  // Staff bypass — never depends on the /auth/me row existing (knwn4official
  // has no users row, which would otherwise spin forever on a 404).
  if (isStaff) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const path = location.pathname

  // No row yet (user.created webhook still in flight right after signup) or
  // /auth/me failed — default a non-staff session into onboarding rather than
  // hanging on a spinner. The row will exist by the time the wizard needs it.
  const user = data?.data?.user
  if (!user) {
    return path === '/onboarding' ? <>{children}</> : <Navigate to="/onboarding" replace />
  }
  const onboarded = user.onboarding_completed === 1
  const entitled = user.entitled === true

  // Post-checkout grace window: show a "confirming payment" screen while we
  // wait for the webhook, rather than flashing the payment wall again.
  if (justPaid && !entitled && polls < POST_CHECKOUT_MAX_POLLS) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Confirming your payment…</p>
      </div>
    )
  }

  // ----- Stage 1: onboarding -----
  if (!onboarded) {
    // Force every route to /onboarding until it's finished.
    return path === '/onboarding' ? <>{children}</> : <Navigate to="/onboarding" replace />
  }
  // Onboarding done but still sitting on /onboarding → push forward.
  if (path === '/onboarding') {
    return <Navigate to={entitled ? '/dashboard' : '/billing'} replace />
  }

  // ----- Stage 2: payment -----
  if (!entitled) {
    // Onboarding finished, no active subscription → force the payment wall.
    return path === '/billing' ? <>{children}</> : <Navigate to="/billing" replace />
  }
  // Entitled but landed on /billing (e.g. back button after paying) → home.
  if (path === '/billing') {
    return <Navigate to="/dashboard" replace />
  }

  // ----- Cleared both gates -----
  return <>{children}</>
}
